import { inject, Injectable } from '@angular/core';
import { limit, orderBy, query, QueryConstraint, setDoc, SetOptions, where } from 'firebase/firestore';
import { CashRegisterStatus } from '@shared';
import { convertToDate } from '@shared/helpers/convert-to-date';
import { firestoreDeepClone } from '@shared/helpers/firestore-deep-clone';
import { IReplicableEntity } from '@shared/models/replicable-entity';
import { ReplicationSyncStatus } from '@shared/models/replication-sync-status';
import { SalesOrderV3 } from '@shared/models/sales-order-v3';
import { BaseReplicableService } from './base-replicable.service';
import { CashRegisterService } from './cash-register/cash-register.service';
import { EntityConfig } from './replication/entity-config';

/**
 * Replicable service for POS sales orders (Order 3.0). Opts into the generic
 * replicate → sync → recover pipeline: an order is written to Firestore (the
 * offline-first source of truth) first, mirrored to the Desktop store through
 * the generic replication batch, pushed to the backend by
 * {@link SalesOrderV3SyncService}, and restored from the Desktop on recovery.
 *
 * @see US 26848 — Replicate & Restore (Order 3.0)
 */
@Injectable({
  providedIn: 'root'
})
export class SalesOrderV3ReplicationService extends BaseReplicableService<SalesOrderV3> {
  protected readonly entityType = 'salesOrderV3';
  protected readonly endpoint = 'SalesOrdersV3';
  protected readonly firestoreCollection = 'salesOrdersV3';

  protected override readonly replicationEnabled = true;
  protected override readonly defaultSyncStatus = ReplicationSyncStatus.UnSynced;
  // Restored after the cash entities (priority 1) so a recovered order always
  // finds its register/transaction context already in place.
  protected override readonly syncPriority = 2;

  /**
   * Persist a POS-created order locally and replicate it to the Desktop. The
   * record starts `UnSynced`; {@link SalesOrderV3SyncService} pushes it to the
   * backend afterwards. Never blocks on the Desktop or the network.
   */
  async createOrder(order: SalesOrderV3): Promise<SalesOrderV3> {
    // Keep `orderDate` a real Date so Firestore stores a Timestamp — the
    // sales-order queries filter and sort on it.
    order.orderDate = order.orderDate
      ? convertToDate(order.orderDate)
      : this.firestoreService.serverDateTime();
    this.ensureValidMetadata(order);
    // A newly created order is always local-first, even when it was cloned
    // from a record that carried an older sync status.
    order.metadata!.syncInfo = {
      ...(order.metadata!.syncInfo ?? {}),
      syncStatus: ReplicationSyncStatus.UnSynced
    };
    return this.create(order);
  }

  /**
   * Mirror an order the backend already owns (placed/paid/fulfilled online)
   * into the local store and the Desktop replica, stamped `Synced` so the
   * backend-sync pipeline leaves it alone. Replaces the old dedicated
   * `POST/PUT {native}/SalesOrdersV3` write path.
   *
   * MERGED into the stored document, never replacing it — the mirrored copy is
   * the backend's projection of the order, so anything only the local document
   * knows (hold details, replication stamps, custom attributes the backend does
   * not echo, the original `createdBy`) would otherwise be dropped on every
   * pay/fulfil/delivery. The old `setDoc(..., { merge: true })` had the same
   * rule.
   */
  async mirrorSyncedOrder(order: SalesOrderV3): Promise<SalesOrderV3> {
    this.ensureValidMetadata(order);
    order.metadata!.syncInfo = {
      ...(order.metadata!.syncInfo ?? {}),
      syncStatus: ReplicationSyncStatus.Synced,
      syncedDate: new Date(),
      syncedBy: this.getSanitizedUserProfile()
    };
    if (order.orderDate) {
      order.orderDate = convertToDate(order.orderDate);
    }
    order.metadata!.updatedDate = this.firestoreService.serverDateTime();
    order.metadata!.updatedBy = this.getSanitizedUserProfile();
    await this.saveToFirestore(order, { merge: true });
    if (this.replicationEnabled && this.shouldTriggerReplication(order)) {
      this.emitReplicationEvent('update', order);
    }
    return order;
  }

  /**
   * The original push selection, in its original order and budget: pending
   * orders newest first, then — the Service Worker only, never the main-thread
   * fallback — rejected orders due for another attempt, then stale claims,
   * `maxRecords` in total (the old `getPendingOrProcessingSalesOrdersV3(1000)`).
   */
  async selectForPushAsync(
    includeRejected: boolean,
    maxRecords = 1000,
    olderThanMs = 10 * 60 * 1000
  ): Promise<SalesOrderV3[]> {
    let remaining = maxRecords;
    const unsynced = await this.getUnsyncedAsync(remaining);
    remaining -= unsynced.length;
    const rejected = includeRejected && remaining > 0
      ? await this.getRetryableFailedAsync(olderThanMs, remaining)
      : [];
    remaining -= rejected.length;
    const stale = remaining > 0 ? await this.getStaleSyncingAsync(olderThanMs, remaining) : [];
    return [...unsynced, ...rejected, ...stale];
  }

  /**
   * Orders that still need to be pushed to the backend (Part 2 — Sync):
   * this cashier's, newest first, as the original `Pending` query read them.
   */
  async getUnsyncedAsync(maxRecords = 1000): Promise<SalesOrderV3[]> {
    return this.queryOrders([
      ...this.ownedByCashier(ReplicationSyncStatus.UnSynced),
      orderBy('orderDate', 'desc'),
      limit(maxRecords)
    ]);
  }

  /**
   * Orders the backend rejected on an earlier cycle that are due for another
   * attempt — the original `ConflictOrError` query, clause for clause: the
   * attempt started (`processingStartedAt`) more than `olderThanMs` ago,
   * oldest attempt first; an order with no start stamp does not match, exactly
   * as before. `Failed` is a visible marker for the orders grid, not a terminal
   * state, but without this gate a permanently rejected backlog was re-pushed
   * (and rewritten Failed → Syncing → Failed) on every sale.
   */
  async getRetryableFailedAsync(olderThanMs = 10 * 60 * 1000, maxRecords = 1000): Promise<SalesOrderV3[]> {
    return this.queryOrders([
      ...this.ownedByCashier(ReplicationSyncStatus.Failed),
      where('metadata.syncInfo.processingStartedAt', '<', new Date(Date.now() - olderThanMs)),
      orderBy('metadata.syncInfo.processingStartedAt'),
      limit(maxRecords)
    ]);
  }

  /**
   * Stale `Syncing` claims left behind by an interrupted push (e.g. a crash
   * between "mark Syncing" and the backend response) — the original
   * `Processing` query: claims whose attempt started more than `olderThanMs`
   * ago, oldest first. The 10-minute default is the original in-flight grace
   * period, and it matches the Service Worker's 10-minute request timeout, so
   * a chunk still in flight is never re-claimed.
   */
  async getStaleSyncingAsync(olderThanMs = 10 * 60 * 1000, maxRecords = 1000): Promise<SalesOrderV3[]> {
    return this.queryOrders([
      ...this.ownedByCashier(ReplicationSyncStatus.Syncing),
      where('metadata.syncInfo.processingStartedAt', '<', new Date(Date.now() - olderThanMs)),
      orderBy('metadata.syncInfo.processingStartedAt'),
      limit(maxRecords)
    ]);
  }

  /**
   * The processing stamps the original push recorded with every transition —
   * `processingStartedAt` when an order is claimed, `processingCompletedAt`
   * when the backend has answered — written atomically with the status by the
   * SyncStatusManager and mirrored onto the in-memory order so the replicated
   * payload matches what was persisted. The retry gates read the start stamp.
   */
  private buildProcessingStamps(order: SalesOrderV3, status: ReplicationSyncStatus): Record<string, unknown> {
    const now = new Date();
    const syncInfo = order.metadata!.syncInfo ?? (order.metadata!.syncInfo = { syncStatus: status });
    switch (status) {
      case ReplicationSyncStatus.Syncing:
        syncInfo.processingStartedAt = now;
        return { 'metadata.syncInfo.processingStartedAt': now };
      case ReplicationSyncStatus.Synced:
      case ReplicationSyncStatus.Failed:
        syncInfo.processingCompletedAt = now;
        return { 'metadata.syncInfo.processingCompletedAt': now };
      default:
        return {};
    }
  }

  async getByLocalIdAsync(localId: string): Promise<SalesOrderV3 | undefined> {
    return this.getFromFirestore(localId);
  }

  /**
   * Locally-stored orders for a fulfillment location — the offline source for
   * the available-to-sell calculation, which used to read them from the
   * Desktop's dedicated sales-order store.
   *
   * Every local order counts regardless of `syncStatus`; callers de-duplicate
   * against the server list by `localId`. The date window is part of the
   * query, never a post-filter — the location's whole history is not
   * downloaded to keep a slice of it — and the result is newest first, so the
   * cap keeps the recent orders, the ones that can still be unposted. Needs
   * the composite index (fulfillmentLocation.id, organizationInfo.id,
   * orderDate desc).
   */
  async getByFulfillmentLocationAsync(
    fulfillmentLocationId: string | undefined,
    orderDateFrom?: Date | string | null,
    maxRecords = 1000
  ): Promise<SalesOrderV3[]> {
    if (!fulfillmentLocationId) return [];

    const from = orderDateFrom ? this.toValidDate(orderDateFrom) : undefined;
    const constraints: QueryConstraint[] = [
      where('metadata.organizationInfo.id', '==', this.authService.currentTenant),
      where('fulfillmentLocation.id', '==', fulfillmentLocationId),
      ...(from ? [where('orderDate', '>=', from)] : []),
      orderBy('orderDate', 'desc'),
      limit(maxRecords)
    ];
    const results = await this.firestoreService.getCollectionAsync<SalesOrderV3>(
      query(this.firestoreService.col<SalesOrderV3>(this.firestoreCollection), ...constraints)
    );
    results.forEach(order => this.normalizeDates(order));
    return results;
  }

  /**
   * Persist a record recovered from the Desktop into Firestore without
   * re-emitting a replication event (it already exists on the Desktop).
   */
  async restoreToFirestore(entity: SalesOrderV3): Promise<void> {
    await this.saveToFirestore(entity);
  }

  /**
   * Adds the recovery scopes on top of the standard config — the open closure
   * for already-`Synced` orders ({@link createOpenClosureFilter}), the current
   * cashier's pending orders for the rest ({@link createPendingRestoreFilter})
   * — the original restore rule of never overwriting a document Firestore
   * already holds, and the processing stamps every status transition writes
   * ({@link buildProcessingStamps}).
   */
  protected override getEntityConfig(): EntityConfig {
    return {
      ...super.getEntityConfig(),
      createSyncedRestoreFilter: () => this.createOpenClosureFilter(),
      createUnsyncedRestoreFilter: () => Promise.resolve(this.createPendingRestoreFilter()),
      restoreOnlyIfMissing: true,
      additionalStatusFields: (entity, status) => this.buildProcessingStamps(entity as SalesOrderV3, status),
      // Restores go through this service so the record lands with real Date
      // values (the Desktop returns ISO strings) — the closure calculation and
      // the orders grid filter those fields with range queries.
      saveRestoredEntity: entity => this.restoreToFirestore(entity as SalesOrderV3)
    };
  }

  /**
   * Sales orders carry Date fields (`orderDate`, payment/delivery dates) that
   * are queried with range and `orderBy` filters, so they must reach Firestore
   * as Timestamps. `firestoreDeepClone` preserves `Date` instances, unlike the
   * base implementation's JSON round-trip which would turn them into strings.
   */
  protected override async saveToFirestore(entity: SalesOrderV3, options?: SetOptions): Promise<void> {
    const documentRef = this.firestoreService.doc(
      `${this.firestoreCollection}/${this.getDocumentId(entity)}`
    );
    this.normalizeDates(entity);
    const plainObject = firestoreDeepClone(entity);
    await this.firestoreService.settleWrite(
      () => (options ? setDoc(documentRef, plainObject, options) : setDoc(documentRef, plainObject)),
      documentRef.path
    );
  }

  /**
   * Recovery scope for orders still waiting for the backend — the original
   * restore (`Find?…&UserId&Status=Pending`) clause for clause: only orders
   * still `UnSynced` (never a claimed or rejected one), and only the current
   * cashier's own, matched on the user id the Desktop matched on (the e-mail
   * only for a record that carries no id). A shared Desktop holds every
   * cashier's orders, and this device only ever pushes the ones its user
   * created, so restoring the others here would park them in this browser
   * without anything to push them.
   */
  private createPendingRestoreFilter(): (entity: IReplicableEntity) => boolean {
    // The raw profile id: it is what `createdBy` carries (the sanitized
    // profile) and what the original restore sent as `UserId` —
    // `currentUserId` strips the `auth0|` prefix and would never match.
    const userId = this.authService.userProfile?.id;
    const email = this.authService.currentUserEmail;
    return entity => {
      if (entity.metadata?.syncInfo?.syncStatus !== ReplicationSyncStatus.UnSynced) return false;
      const createdBy = entity.metadata?.createdBy;
      return createdBy?.id
        ? !!userId && createdBy.id === userId
        : !!email && createdBy?.email === email;
    };
  }

  /**
   * Recovery scope for orders the backend already accepted: only those in the
   * cash register's CURRENTLY OPEN closure are worth restoring — they still
   * feed the open-closure totals and receipts. Anything older is history the
   * backend owns. With no open register there is no open closure, so no
   * `Synced` order is restored.
   */
  private async createOpenClosureFilter(): Promise<(entity: IReplicableEntity) => boolean> {
    const cashRegister = await this.findOpenCashRegister();
    if (!cashRegister) return () => false;

    const registerId = cashRegister.id;
    const openedAtMs = this.toMillis(cashRegister.opening_details?.opening_date);

    return (entity: IReplicableEntity) => {
      const order = entity as SalesOrderV3;
      if (registerId && order.cashRegister?.id && order.cashRegister.id !== registerId) {
        return false;
      }
      if (!openedAtMs) return true;
      const orderAtMs = this.toMillis(
        order.extraAttributes?.businessDay ?? order.orderDate ?? order.metadata?.createdDate
      );
      return orderAtMs >= openedAtMs;
    };
  }

  /**
   * The cash register store. Read through the facade rather than the Desktop:
   * after Phase 2 nothing writes the Desktop's register document, so a Desktop
   * read would silently go stale and mis-scope this restore.
   */
  private readonly cashRegisterService = inject(CashRegisterService);

  /** The current cashier's register, only when it is open. */
  private async findOpenCashRegister(): Promise<any | undefined> {
    const email = this.authService.currentUserEmail;
    if (!email) return undefined;

    const cashRegister = await this.cashRegisterService.findByActivatedUserAsync(email);
    return cashRegister?.status === CashRegisterStatus.Open ? cashRegister : undefined;
  }

  /** The tenant + cashier + status equalities every push query starts with. */
  private ownedByCashier(status: ReplicationSyncStatus): QueryConstraint[] {
    return [
      where('metadata.organizationInfo.id', '==', this.authService.currentTenant),
      where('metadata.createdBy.email', '==', this.authService.userProfile?.email),
      where('metadata.syncInfo.syncStatus', '==', status)
    ];
  }

  /**
   * Run a push query. These carry an `orderBy` (and a range on the same field),
   * so each shape needs its composite index — the same shapes the pre-cutover
   * queries had on this collection.
   */
  private async queryOrders(constraints: QueryConstraint[]): Promise<SalesOrderV3[]> {
    const results = await this.firestoreService.getCollectionAsync<SalesOrderV3>(
      query(this.firestoreService.col<SalesOrderV3>(this.firestoreCollection), ...constraints)
    );
    results.forEach(order => this.normalizeDates(order));
    return results;
  }

  /**
   * Normalize the order's date fields to real `Date` instances, in both
   * directions: Firestore hands back `Timestamp`s (the backend sync payload
   * expects dates), and the backend hands back ISO strings (Firestore must
   * store Timestamps, because the closure calculation and the orders grid
   * filter these fields with range queries). Unparseable values are left
   * untouched rather than turned into an Invalid Date.
   */
  private normalizeDates(order: SalesOrderV3): void {
    order.orderDate = this.toValidDate(order.orderDate) ?? order.orderDate;
    order.paymentDate = this.toValidDate(order.paymentDate) ?? order.paymentDate;
    order.deliveryDate = this.toValidDate(order.deliveryDate) ?? order.deliveryDate;
    order.fulfillmentDate = this.toValidDate(order.fulfillmentDate) ?? order.fulfillmentDate;
    order.salesRecognitionDate = this.toValidDate(order.salesRecognitionDate) ?? order.salesRecognitionDate;
    order.cancelDate = this.toValidDate(order.cancelDate) ?? order.cancelDate;
    if (order.extraAttributes?.businessDay) {
      order.extraAttributes.businessDay =
        this.toValidDate(order.extraAttributes.businessDay) ?? order.extraAttributes.businessDay;
    }
    if (order.metadata?.createdDate) {
      order.metadata.createdDate = this.toValidDate(order.metadata.createdDate) ?? order.metadata.createdDate;
    }
  }

  /** A valid `Date` for Timestamp/ISO-string/Date input, otherwise undefined. */
  private toValidDate(value: unknown): Date | undefined {
    if (!value) return undefined;
    const date = convertToDate(value as any);
    return date instanceof Date && !isNaN(date.getTime()) ? date : undefined;
  }

  /**
   * Normalize a date that may be a Firestore Timestamp, an ISO string, or a
   * Date into epoch milliseconds (0 when absent/invalid).
   */
  private toMillis(value: unknown): number {
    if (!value) return 0;
    const date = convertToDate(value as any);
    const time = date instanceof Date ? date.getTime() : NaN;
    return isNaN(time) ? 0 : time;
  }
}
